/**
 * Shared primitives for the "Your record" pages that live inside AppShell
 * (Profile, Publications, Recommenders, Applications, Attorneys).
 * Design system v2: one page column, sentence-case eyebrow, named type scale.
 */
import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";

export function PageFrame({
  children,
  width = "wide",
}: {
  children: ReactNode;
  /** "form" = 720px column, "wide" = 1080px. */
  width?: "form" | "wide";
}) {
  return (
    <div
      className={`mx-auto w-full ${width === "form" ? "max-w-[720px]" : "max-w-[1080px]"} px-4 pb-16 pt-8 sm:px-6 sm:pt-10`}
    >
      {children}
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1 className="mt-1 font-display text-h2 text-ink sm:text-h1">
          {title}
        </h1>
        {subtitle && <p className="mt-2 max-w-[60ch] text-body text-ink-2">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

const TONES = {
  info: { box: "border-line bg-surface text-ink-1", icon: Info, iconCls: "text-ink-3" },
  warn: { box: "border-warn bg-amber-50 text-amber-600", icon: AlertTriangle, iconCls: "text-amber-600" },
  error: { box: "border-terra-100 bg-terra-50 text-terra-700", icon: XCircle, iconCls: "text-terra-600" },
  success: { box: "border-sage-100 bg-sage-50 text-sage-700", icon: CheckCircle2, iconCls: "text-sage-600" },
} as const;

export function Notice({
  tone = "info",
  children,
  onDismiss,
  className = "",
}: {
  tone?: keyof typeof TONES;
  children: ReactNode;
  onDismiss?: () => void;
  className?: string;
}) {
  const t = TONES[tone];
  const Icon = t.icon;
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={`vg-notice flex items-start gap-3 rounded-xl border px-4 py-3 text-small ${t.box} ${className}`}
    >
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${t.iconCls}`} aria-hidden />
      <div className="min-w-0 flex-1">{children}</div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="-m-1 rounded-full p-1 opacity-70 hover:opacity-100"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  children,
  action,
}: {
  icon: typeof Info;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="card flex flex-col items-center px-6 py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-terra-50 text-terra-600">
        <Icon className="h-6 w-6" aria-hidden />
      </span>
      <h2 className="mt-4 font-display text-h2 text-ink">{title}</h2>
      {children && <div className="mt-2 max-w-[46ch] text-body text-ink-2">{children}</div>}
      {action && <div className="mt-6 flex flex-wrap justify-center gap-3">{action}</div>}
    </div>
  );
}
